"use client";
import ArrowDownRightIcon from "@heroicons/react/24/solid/ArrowDownRightIcon";
import MapPinIcon from "@heroicons/react/24/outline/MapPinIcon";
import AvatarDecoration from "@/components/views/avatar-decoration/AvatarDecoration";
import { useEffect, useState } from "react";
import { useVenueDetail } from "@/hooks/venueContext";
import { DEFAULT_IMAGES } from "@/lib/constants";
import WhatsappButton from "@/components/WhatsappButton";
import {
  WHATSAPP_SECTIONS,
  WHATSAPP_TAGS,
} from "@/@core/enums/whatsapp_tags.enum";
import { cn } from "@/lib/utils";
import S3Image from "@/components/S3Image";
import WhatsappIcon from "@/components/hero-icons/whatsapp-icon";
import dynamic from "next/dynamic";

const WorkerDetailModal = dynamic(
  () => import("@/components/views/modals/worker/WorkerDetailModal"),
);

interface Props extends Worker.List {}

export default function CrewCard(trainer: Props) {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [avatarSrc, setAvatarSrc] = useState(
    trainer.profile_picture ? `user/${trainer?.id}/${trainer?.profile_picture}` : DEFAULT_IMAGES.WORKER
  );
  const { setVenue } = useVenueDetail();

  const handleClick = () => {
    setVenue(trainer.job?.venue as Venue.List);
    setModalIsOpen(true);
  };

  const getWorkerImage = () => {
    return trainer.profile_picture ? `user/${trainer?.id}/${trainer?.profile_picture}` : DEFAULT_IMAGES.WORKER
  };

  useEffect(() => {
    setAvatarSrc(getWorkerImage());
  }, [trainer]);

  return (
    <>
      <div
        role="button"
        onClick={() => handleClick()}
        className="flex justify-between items-center p-4 w-full lg:w-full gap-2 border-gray border rounded-[24px]"
      >
        <div className="flex max-md:items-center gap-2">
          <AvatarDecoration className="rounded-3xl md:rounded-2xl">
            {/*<object*/}
            {/*  className="w-20 h-20 md:w-[92px] md:h-[92px] object-cover object-center rounded-3xl md:rounded-2xl border border-white"*/}
            {/*  data={getWorkerImage()}*/}
            {/*  type="image/jpg"*/}
            {/*>*/}
            <S3Image
              height={90}
              width={90}
              sizes="90px"
              className="w-[90px] h-[90px] object-cover object-center rounded-3xl md:rounded-2xl border border-white"
              src={avatarSrc}
              onError={() => setAvatarSrc(DEFAULT_IMAGES.WORKER)}
              loading="lazy"
              alt="trainer"
              style={{
                maxWidth: "100%",
              }}
            />
            {/*</object>*/}
          </AvatarDecoration>

          <div className="flex flex-col justify-between">
            <div className="flex flex-col gap-2">
              <h3 className="font-bold text-base">
                {trainer.firstname} {trainer.lastname}
              </h3>

              <span className="font-medium text-xs text-assistant">
                {trainer.job?.occupation?.descriptions[0]?.title}
              </span>
            </div>

            <div className="hidden md:flex flex-col gap-1">
              <div className="flex items-center gap-1">
                <ArrowDownRightIcon className="size-4 text-assistant" />
                <span className="text-ellipsis line-clamp-1 text-gray-400 text-xs font-medium">
                  {trainer.job?.venue?.descriptions[0]?.title}
                </span>
              </div>

              <div className="flex items-center gap-1">
                <MapPinIcon className="size-4 text-assistant" />
                <span className="text-gray-400 text-[10px] font-medium">
                  {trainer.job?.venue?.district.title}
                </span>
              </div>
            </div>
          </div>
        </div>

        {trainer.phone && (
          <WhatsappButton
            section={WHATSAPP_SECTIONS.TRAINER}
            tag={WHATSAPP_TAGS.WORKER}
            translateValues={{
              trainer: `${trainer?.firstname} ${trainer?.lastname}`,
            }}
            phoneNumber={trainer.phone ? trainer.phone : ""}
            venue={trainer.job?.venue as Venue.List}
            className={cn(
              "flex cursor-pointer active:!mx-0 !h-6 !py-1 !px-2 items-center justify-center",
              !trainer.phone ? "cursor-not-allowed" : "",
            )}
          >
            <WhatsappIcon className="size-4" />
          </WhatsappButton>
        )}
      </div>

      {modalIsOpen && (
        <WorkerDetailModal
          worker={trainer as Worker.List}
          isOpen={modalIsOpen}
          onClose={() => setModalIsOpen(false)}
        />
      )}
    </>
  );
}
